import { useEffect, useState } from 'react';
import { useStore } from '../store.js';

function TurnTimer({ endsAt }: { endsAt: number }) {
  const [remaining, setRemaining] = useState(() => Math.max(0, endsAt - Date.now()));

  useEffect(() => {
    const id = setInterval(() => setRemaining(Math.max(0, endsAt - Date.now())), 200);
    return () => clearInterval(id);
  }, [endsAt]);

  const seconds = Math.ceil(remaining / 1000);
  return (
    <span className={`timer ${seconds <= 10 ? 'timer--urgent' : ''}`} aria-label={`${seconds} seconds left`}>
      {seconds}s
    </span>
  );
}

/**
 * The "last card" call.
 *
 * The announcement has to ride along with the play that takes you down to one
 * card — by the time you are holding one, the turn has already passed. Making
 * the player remember that was a trap, so it arms itself and simply explains
 * what it is about to do. Turning it off is still allowed.
 */
export function ActionBar() {
  const game = useStore((s) => s.game);
  const selectedCardIds = useStore((s) => s.selectedCardIds);
  const playSelected = useStore((s) => s.playSelected);
  const passTurn = useStore((s) => s.passTurn);
  const clearSelection = useStore((s) => s.clearSelection);

  if (!game) return null;

  const yourTurn = game.currentPlayerId === game.yourPlayerId;
  const canPass = game.yourLegalMoves.some((m) => m.kind === 'pass');
  const current = game.players.find((p) => p.id === game.currentPlayerId);

  const drawMove = game.yourLegalMoves.find((m) => m.kind === 'draw');
  const hasPlay = game.yourLegalMoves.some((m) => m.kind === 'play');
  // A player holding nothing legal needs telling, not left guessing.
  const drawHint =
    yourTurn && drawMove?.kind === 'draw' && !hasPlay
      ? drawMove.reason === 'penalty'
        ? `Pick up ${drawMove.count}`
        : 'Nothing to play — draw a card'
      : null;

  return (
    <div className="actionbar">
      <div className="actionbar__status">
        {game.phase === 'finished' ? (
          <span>Game over</span>
        ) : yourTurn ? (
          <>
            <span className="actionbar__turn">Your turn</span>
            {game.turnEndsAt && <TurnTimer endsAt={game.turnEndsAt} />}
          </>
        ) : (
          <span className="actionbar__waiting">Waiting for {current?.name ?? '…'}</span>
        )}
        {game.pending && (
          <span className="actionbar__pending">
            {game.pending.label}: pick up {game.pending.amount}
          </span>
        )}
        {drawHint && <span className="actionbar__hint">{drawHint}</span>}
      </div>

      <div className="actionbar__buttons">
        {selectedCardIds.length > 0 && (
          <>
            <button type="button" className="btn btn--ghost" onClick={clearSelection}>
              Clear
            </button>
            <button type="button" className="btn btn--primary" onClick={playSelected} disabled={!yourTurn}>
              Play {selectedCardIds.length > 1 ? `${selectedCardIds.length} cards` : 'card'}
            </button>
          </>
        )}

        {canPass && (
          <button type="button" className="btn btn--secondary" onClick={passTurn}>
            Pass
          </button>
        )}
      </div>
    </div>
  );
}
